import api from './api';
import { mockInitialPosts, uid } from '../utils/mockData';

const USE_MOCK = import.meta.env.VITE_USE_MOCK !== 'false';
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// In-memory mock store (survives during session)
let mockStore = [...mockInitialPosts];

/**
 * Normalize a post payload coming from the backend (snake_case + nested
 * relations) into the camelCase shape the UI reads. Mock posts already
 * match this shape, so the UI is the same in both modes.
 */
function normalizePost(p) {
  if (!p) return null;
  return {
    id: p.id,
    caption: p.caption ?? '',
    hashtags: Array.isArray(p.hashtags) ? p.hashtags : [],
    scheduledAt: p.scheduled_at ?? p.scheduledAt ?? null,
    publishedAt: p.published_at ?? p.publishedAt ?? null,
    status: p.status,
    attempts: p.attempts ?? 0,
    lastError: p.last_error ?? p.lastError ?? null,
    // Accept either [string] (mock) or [{ platform, status }] (backend).
    platforms: (p.platforms || []).map((pp) =>
      typeof pp === 'string' ? pp : pp.platform
    ),
    // Accept either [string] (mock) or [{ url }] (backend).
    mediaUrls: Array.isArray(p.media) && p.media.length
      ? p.media.map((m) => m.url).filter(Boolean)
      : (p.mediaUrls || []),
    socialAccountIds: (p.platforms || [])
      .map((pp) => (typeof pp === 'string' ? null : pp.social_account_id))
      .filter(Boolean),
    campaignId: p.campaign_id ?? p.campaignId ?? null,
    // Denormalized campaign embed (matches PostResource shape).
    campaign: p.campaign ?? null,
    createdAt: p.created_at ?? p.createdAt ?? null,
    updatedAt: p.updated_at ?? p.updatedAt ?? null,
  };
}

/**
 * Build a multipart FormData body for create/update when the payload
 * contains File objects in `media`. Arrays are sent with the [] suffix
 * so Laravel's request()->array(...) parses them correctly.
 */
function buildFormData(payload) {
  const fd = new FormData();
  for (const [key, value] of Object.entries(payload)) {
    if (value === null || value === undefined) continue;

    if (key === 'media' && Array.isArray(value)) {
      for (const item of value) {
        // Accept either a raw File or the { file, url } wrapper from FileUpload.
        const file = item instanceof File ? item : item?.file;
        if (file instanceof File) fd.append('media[]', file);
      }
      continue;
    }
    if (Array.isArray(value)) {
      for (const v of value) fd.append(`${key}[]`, v);
      continue;
    }
    if (typeof value === 'object') {
      fd.append(key, JSON.stringify(value));
      continue;
    }
    fd.append(key, value);
  }
  return fd;
}

/**
 * Extract the payload for a real-backend request from the frontend's
 * camelCase payload. Drops mock-only fields (mediaUrls, scheduledAt camel
 * duplicate) and maps media objects → File list so FormData can serialize.
 */
function toApiPayload(payload) {
  const {
    caption,
    hashtags,
    platforms,
    social_account_ids,
    scheduled_at,
    scheduledAt,
    status,
    mediaUrls,
    media,
    ...rest
  } = payload;

  void mediaUrls; // dropped — real backend receives binary files instead

  return {
    caption,
    hashtags,
    platforms,
    social_account_ids,
    // Prefer snake_case; fall back to camelCase only if the caller forgot.
    scheduled_at: scheduled_at ?? scheduledAt ?? null,
    status,
    media,
    ...rest,
  };
}

export const postService = {
  async list(params = {}) {
    if (USE_MOCK) {
      await delay(300);
      let results = [...mockStore];
      if (params.status) {
        results = results.filter((p) => p.status === params.status);
      }
      if (params.campaign_id !== undefined && params.campaign_id !== null && params.campaign_id !== '') {
        if (params.campaign_id === 'none' || params.campaign_id === 'null') {
          results = results.filter((p) => !p.campaign_id);
        } else {
          const id = Number(params.campaign_id);
          results = results.filter((p) => p.campaign_id === id);
        }
      }
      return results
        .sort((a, b) => {
          const at = a.scheduledAt || a.createdAt;
          const bt = b.scheduledAt || b.createdAt;
          return new Date(bt) - new Date(at);
        })
        .map(normalizePost);
    }
    const { data } = await api.get('/posts', { params });
    // Laravel paginator keeps { data: [...], meta, links } even with
    // withoutWrapping() — unwrap the array here.
    const list = Array.isArray(data) ? data : data?.data || [];
    return list.map(normalizePost);
  },

  async create(payload) {
    if (USE_MOCK) {
      await delay(400);
      const newPost = {
        id: uid('post'),
        ...payload,
        createdAt: new Date().toISOString(),
      };
      mockStore = [newPost, ...mockStore];
      return normalizePost(newPost);
    }
    const apiPayload = toApiPayload(payload);
    // Switch to multipart when files are present; otherwise JSON.
    const hasFiles =
      Array.isArray(apiPayload.media) &&
      apiPayload.media.some((m) => m instanceof File || m?.file instanceof File);

    const { data } = hasFiles
      ? await api.post('/posts', buildFormData(apiPayload), {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      : await api.post('/posts', apiPayload);

    return normalizePost(data);
  },

  async update(id, patch) {
    if (USE_MOCK) {
      await delay(300);
      let updated = null;
      mockStore = mockStore.map((p) => {
        if (p.id === id) {
          updated = { ...p, ...patch };
          return updated;
        }
        return p;
      });
      if (!updated) throw new Error('Post not found');
      return normalizePost(updated);
    }
    const apiPayload = toApiPayload(patch);
    const hasFiles =
      Array.isArray(apiPayload.media) &&
      apiPayload.media.some((m) => m instanceof File || m?.file instanceof File);

    // Laravel doesn't parse multipart on PUT/PATCH — use POST + _method spoof.
    let response;
    if (hasFiles) {
      const fd = buildFormData({ ...apiPayload, _method: 'PATCH' });
      response = await api.post(`/posts/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else {
      response = await api.patch(`/posts/${id}`, apiPayload);
    }
    return normalizePost(response.data);
  },

  async remove(id) {
    if (USE_MOCK) {
      await delay(200);
      mockStore = mockStore.filter((p) => p.id !== id);
      return { success: true };
    }
    await api.delete(`/posts/${id}`);
    return { success: true };
  },

  async reschedule(id, scheduledAt) {
    return this.update(id, {
      scheduled_at: scheduledAt,
      scheduledAt,
      status: 'scheduled',
    });
  },

  /** POST /api/posts/{id}/publish — enqueue immediate publish. */
  async publishNow(id) {
    if (USE_MOCK) {
      await delay(200);
      return { message: 'Publish queued (mock)' };
    }
    const { data } = await api.post(`/posts/${id}/publish`);
    return data;
  },

  /** POST /api/posts/{id}/retry — retry failed platforms. */
  async retry(id) {
    if (USE_MOCK) {
      await delay(200);
      return { message: 'Retry queued (mock)' };
    }
    const { data } = await api.post(`/posts/${id}/retry`);
    return data;
  },

  /**
   * GET /api/posts/failures — feed for the Failure & Reliability Center.
   * Mock mode synthesizes rows from mockStore posts that happen to be in
   * a failed state so the dashboard renders something useful without a
   * running backend.
   */
  async listFailures(params = {}) {
    if (USE_MOCK) {
      await delay(250);
      const suggestions = {
        auth: { code: 'reconnect_account', text: 'Reconnect this social account from the Accounts page.' },
        media: { code: 'replace_media', text: 'Remove or replace the attached media, then retry.' },
        quota: { code: 'wait_and_retry', text: 'Wait a few minutes and retry — the platform is rate-limiting us.' },
        network: { code: 'retry_now', text: 'Transient network issue — retry now.' },
        validation: { code: 'edit_post', text: 'Edit the post to fix the validation error.' },
        unknown: { code: 'retry_later', text: 'Temporary provider error — retry in a few minutes.' },
      };
      const categories = Object.keys(suggestions);
      // Build synthetic failure rows from mock posts.
      const base = mockStore
        .filter((p) => p.status === 'failed' || p.status === 'draft')
        .slice(0, 5);
      const extras = mockStore.slice(0, 3).map((p, i) => ({
        ...p,
        status: 'failed',
        id: `${p.id}_f${i}`,
      }));
      const source = [...base, ...extras];
      const rows = source.map((p, i) => {
        const category = categories[i % categories.length];
        return {
          id: 1000 + i,
          post_id: p.id,
          platform: p.platforms[i % p.platforms.length] || 'facebook',
          status: 'failed',
          attempts: 1 + (i % 3),
          failed_at: new Date(Date.now() - i * 3600_000).toISOString(),
          error: [
            'Access token expired for this account.',
            'Media file too large.',
            'Rate limit exceeded — too many requests.',
            'Connection timeout to API.',
            'Caption too long.',
            'Unauthorized — token revoked.',
          ][i % 6],
          error_code: ['token_expired', 'media_too_large', 'rate_limited', 'timeout', 'caption_too_long', 'token_revoked'][i % 6],
          error_category: category,
          suggestion: suggestions[category],
          is_retryable: !['auth', 'validation'].includes(category),
          post: {
            id: p.id,
            caption: p.caption,
            scheduled_at: p.scheduledAt,
          },
          account: {
            id: i + 1,
            account_name: '@demo_account',
            status: category === 'auth' ? 'revoked' : 'active',
          },
        };
      });

      const filtered = params.category
        ? rows.filter((r) => r.error_category === params.category)
        : rows;
      return filtered;
    }
    const { data } = await api.get('/posts/failures', { params });
    return Array.isArray(data) ? data : data?.data || [];
  },
};
